import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["initials"];
import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'foundations-components/transitional/components/Button';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { View } from 'foundations-components/transitional/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { ShareModal } from './ShareModal';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useLoginVerifyQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc.hooks';
import { Code } from 'foundations-components/transitional/components/Code';
import { getPortalInstanceId, getPortalAccountId } from 'portals-platform-lib';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function UserAvatar({
  initials,
  size = 'medium'
}) {
  const dimensions = size === 'small' ? '32px' : '40px';
  return /*#__PURE__*/_jsx(Flex, {
    backgroundColor: "fill-accent-teal-default",
    borderRadius: "full",
    inlineSize: dimensions,
    blockSize: dimensions,
    alignItems: "center",
    justifyContent: "center",
    children: /*#__PURE__*/_jsx(Text, {
      typography: size === 'small' ? 'detail-100' : 'body-100',
      color: "text-inverse-default",
      children: initials
    })
  });
}
function AccountButton(_ref) {
  let {
      initials
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(Button, Object.assign({}, props, {
    use: "unstyled",
    "aria-label": "My Account",
    children: /*#__PURE__*/_jsx(UserAvatar, {
      initials: initials,
      size: "small"
    })
  }));
}
export function MyAccountPopover() {
  var _loginVerifyError$net;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const handleShareClick = () => {
    setIsShareModalOpen(true);
  };
  const {
    data: loginVerifyData,
    loading: loginVerifyLoading,
    error: loginVerifyError
  } = useLoginVerifyQuery();
  const loginVerifyNetworkError = loginVerifyError === null || loginVerifyError === void 0 || (_loginVerifyError$net = loginVerifyError.networkError) === null || _loginVerifyError$net === void 0 ? void 0 : _loginVerifyError$net.cause;
  const initials = useMemo(() => {
    if (!loginVerifyData) {
      return 'U';
    }
    const firstName = loginVerifyData.firstname || '';
    const lastName = loginVerifyData.lastname || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  }, [loginVerifyData]);
  const fullName = useMemo(() => {
    if (!loginVerifyData) {
      return 'User';
    }
    const firstName = loginVerifyData.firstname || '';
    const lastName = loginVerifyData.lastname || '';
    return `${firstName} ${lastName}`.trim() || 'User';
  }, [loginVerifyData]);
  const renderButton = useCallback(props => /*#__PURE__*/_jsx(AccountButton, Object.assign({}, props, {
    initials: initials
  })), [initials]);
  if (loginVerifyNetworkError) {
    console.log(`Unauthenticated: ${loginVerifyNetworkError.denialReason}`);
  }
  if (loginVerifyLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "extra-small"
    });
  }
  if (!loginVerifyData) {
    return /*#__PURE__*/_jsx(Button, {
      use: "primary",
      size: "small",
      href: `/_hcms/mem/login?redirect_url=${window.location.href}`,
      children: "Login"
    });
  }

  // const { data: companyData } = useDynamicCompanyFetchPortalMethodQuery({
  //   variables: {
  //     message: 'test',
  //     // hardcoded for now
  //     objectId: 27082891039,
  //   },
  // });
  // console.log('authenticatedCompanyData', companyData);

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Dropdown, {
      buttonUse: "unstyled",
      menuWidth: 240,
      Button: renderButton,
      children: [/*#__PURE__*/_jsx(View, {
        padding: "300",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          columnGap: "300",
          children: [/*#__PURE__*/_jsx(UserAvatar, {
            initials: initials
          }), /*#__PURE__*/_jsxs(View, {
            children: [/*#__PURE__*/_jsx(Paragraph, {
              typography: "body-100",
              marginBlockEnd: "0",
              children: fullName
            }), /*#__PURE__*/_jsx(Paragraph, {
              typography: "detail-100",
              color: "text-core-subtle",
              marginBlockEnd: "0",
              children: loginVerifyData.email || 'N/A'
            })]
          })]
        })
      }), /*#__PURE__*/_jsx(View, {
        padding: "300",
        borderBlockStartWidth: "100",
        borderBlockStartColor: "border-core-subtle",
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          rowGap: "200",
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            columnGap: "100",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              color: "text-core-subtle",
              children: "Account ID:"
            }), /*#__PURE__*/_jsx(Code, {
              children: getPortalAccountId()
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            columnGap: "100",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              color: "text-core-subtle",
              children: "Portal Instance ID:"
            }), /*#__PURE__*/_jsx(Code, {
              children: getPortalInstanceId()
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            columnGap: "100",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              color: "text-core-subtle",
              children: "Contact VID:"
            }), /*#__PURE__*/_jsx(Code, {
              children: loginVerifyData.contactVid ? loginVerifyData.contactVid : 'N/A'
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            columnGap: "100",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              color: "text-core-subtle",
              children: "Phone:"
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              children: loginVerifyData.phone ? loginVerifyData.phone : 'N/A'
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            columnGap: "100",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              color: "text-core-subtle",
              children: "Company:"
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              children: loginVerifyData.company ? loginVerifyData.company : 'N/A'
            })]
          })]
        })
      }), /*#__PURE__*/_jsx(View, {
        padding: "300",
        borderBlockStartWidth: "100",
        borderBlockStartColor: "border-core-subtle",
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          rowGap: "200",
          children: [/*#__PURE__*/_jsx(View, {
            children: /*#__PURE__*/_jsx(Link, {
              to: "/settings",
              children: /*#__PURE__*/_jsx(Button, {
                use: "tertiary-light",
                size: "small",
                block: true,
                children: /*#__PURE__*/_jsx(Flex, {
                  justifyContent: "center",
                  children: "Settings"
                })
              })
            })
          }), /*#__PURE__*/_jsx(View, {
            children: /*#__PURE__*/_jsx(Button, {
              use: "tertiary-light",
              size: "small",
              onClick: handleShareClick,
              block: true,
              children: /*#__PURE__*/_jsx(Flex, {
                justifyContent: "center",
                children: "Share portal"
              })
            })
          }), /*#__PURE__*/_jsx(View, {
            children: /*#__PURE__*/_jsx(Button, {
              use: "primary",
              size: "small",
              href: "/_hcms/mem/logout",
              block: true,
              children: /*#__PURE__*/_jsx(Flex, {
                justifyContent: "center",
                children: "Logout"
              })
            })
          })]
        })
      })]
    }), /*#__PURE__*/_jsx(ShareModal, {
      open: isShareModalOpen,
      onClose: () => setIsShareModalOpen(false)
    })]
  });
}